import type { RouteRecordRaw } from "vue-router"
import callsReportRoutes from "@/router/callsReport"

const leadDistributionRoutes: Array<RouteRecordRaw> = [
  {
    path: "/dashboard",
    name: "dashboard",
    component: () => import("@/views/Dashboard.vue"),
    meta: {
      pageTitle: "Dashboard",
      breadcrumbs: ["Dashboards"],
    },
  },
  {
    path: "/dashboard/buyer-activity",
    name: "buyerActivityDashboard",
    component: () => import("@/views/dashboard/BuyerActivityDashboard.vue"),
    meta: {
      pageTitle: "Buyer activity",
      breadcrumbs: ["Dashboards", "Buyer activity"],
    },
  },
  {
    path: "/lead-distribution",
    name: "leadDistribution",
    children: [
      {
        path: "reporting",
        name: "reporting",
        component: () => import("@/views/lead-distribution/Reporting.vue"),
        meta: {
          pageTitle: "Reporting",
          breadcrumbs: ["Lead Distribution", "Reporting"],
        },
      },
      {
        path: "reporting/:reportId",
        name: "singleReporting",
        component: () => import("@/views/lead-distribution/Reporting.vue"),
        meta: {
          pageTitle: "Reporting",
          breadcrumbs: ["Lead Distribution", "Reporting"],
        },
      },
    ],
  },
  {
    path: "/leads-report",
    name: "leadsReport",
    children: [
      {
        path: "leads",
        name: "leads",
        component: () => import("@/views/lead-distribution/Lead.vue"),
        meta: {
          pageTitle: "Leads",
          breadcrumbs: ["Lead Distribution", "Leads"],
        },
      },
      {
        path: "leads/:leadReportId",
        name: "singleLeadReporting",
        component: () => import("@/views/lead-distribution/Lead.vue"),
        meta: {
          pageTitle: "Leads",
          breadcrumbs: ["Lead Distribution", "Leads"],
        },
      },
    ],
  },
  ...callsReportRoutes,
  {
    name: "platform",
    path: "/platform",
    children: [
      {
        path: "list",
        name: "platformList",
        component: () => import("@/views/lead-distribution/platform/PlatformList.vue"),
        meta: {
          pageTitle: "Lists",
          breadcrumbs: ["Lead Distribution", "Lists"],
        },
      },
      {
        path: "platform-list/:platformId",
        name: "platformDetails",
        component: () => import("@/components/page-layouts/Platform.vue"),
        meta: {
          breadcrumbs: ["Lead Distribution", "List"],
          pageTitle: "Platform Details",
        },
        children: [
          {
            path: "leads",
            name: "platform-leads",
            component: () => import("@/views/lead-distribution/Lead.vue"),
            meta: {
              pageTitle: "Leads",
              breadcrumbs: ["Lead Distribution", "List"],
              parentName: "platformDetails",
            },
          },
          {
            path: "integrations",
            name: "platform-integrations",
            component: () => import("@/views/lead-distribution/platform/Integration.vue"),
            meta: {
              pageTitle: "Integrations",
              breadcrumbs: ["Lead Distribution", "List"],
              parentName: "platformDetails",
            },
          },
          {
            path: "copy-from-another-list",
            name: "copy-from-another-list",
            component: () => import("@/components/platform-list/integrations/CopyFromAnotherList.vue"),
            meta: {
              pageTitle: "Copy from Another List",
              breadcrumbs: ["Lead Distribution", "List", "Copy from Another List"],
              parentName: "platformDetails",
            },
          },
          {
            path: "posting-specs",
            name: "platform-posting-specs",
            component: () => import("@/views/lead-distribution/platform/PostingSpecs.vue"),
            meta: {
              pageTitle: "Posting Specs",
              breadcrumbs: ["Lead Distribution", "List"],
              parentName: "platformDetails",
            },
          },
          {
            path: "headers",
            name: "platform-headers",
            component: () => import("@/views/lead-distribution/platform/Headers.vue"),
            meta: {
              pageTitle: "Headers",
              parentName: "platformDetails",
            },
          },
          {
            path: "criteria",
            name: "platform-criteria",
            component: () => import("@/views/lead-distribution/platform/Criteria.vue"),
            meta: {
              pageTitle: "Criteria",
              parentName: "platformDetails",
            },
          },
          {
            path: "settings",
            name: "platform-settings",
            component: () => import("@/views/lead-distribution/platform/Settings.vue"),
            meta: {
              pageTitle: "Settings",
              parentName: "platformDetails",
            },
          },
          {
            path: "description",
            name: "platform-description",
            component: () => import("@/views/lead-distribution/platform/Description.vue"),
            meta: {
              pageTitle: "Description",
              parentName: "platformDetails",
            },
          },
          {
            path: "transcript-qualification-documents",
            name: "platform-transcript-qualification-documents",
            component: () => import("@/views/lead-distribution/platform/TranscriptionQualificationDocuments.vue"),
            meta: {
              pageTitle: "Transcript Criteria Document",
              parentName: "platformDetails",
            },
          },
        ],
      },
    ],
  },
  {
    path: "caps-overview",
    name: "caps-overview",
    component: () => import("@/views/lead-distribution/CapsOverview.vue"),
    meta: {
      pageTitle: "Caps Overview",
      breadcrumbs: ["Lead Distribution", "Caps Overview"],
    },
  },
  {
    path: "budgets",
    name: "budgets",
    component: () => import("@/views/lead-distribution/Budgets.vue"),
    meta: {
      pageTitle: "Budgets",
      breadcrumbs: ["Lead Distribution", "Budgets"],
    },
  },
  {
    path: "/blacklist",
    name: "blacklist",
    component: () => import("@/views/lead-distribution/blacklist/ManageBlacklist.vue"),
    meta: {
      pageTitle: "Blacklist",
      breadcrumbs: ["Blacklist"],
    },
  },
  {
    path: "/lead-settings/affiliate-id-bundles",
    name: "affiliate-id-bundles",
    component: () => import("@/views/lead-distribution/affiliate-id-bundles/ManageBundles.vue"),
    meta: {
      pageTitle: "Affiliate ID Bundles",
      breadcrumbs: ["Affiliate ID Bundles"],
    },
  },
  {
    path: "/filters",
    name: "filters",
    component: () => import("@/views/lead-distribution/buyer-filter/ManageBuyerFilter.vue"),
    meta: {
      pageTitle: "Filters",
      breadcrumbs: ["Filters"],
    },
  },
  {
    path: "/did-settings",
    name: "did-settings",
    component: () => import("@/views/lead-distribution/did-settings/ManageDIDSettings.vue"),
    meta: {
      pageTitle: "DID Settings",
      breadcrumbs: ["DID Settings"],
    },
  },
  {
    path: "/lead-settings",
    name: "lead-settings",
    children: [
      {
        path: "spam-settings",
        name: "spam-settings",
        component: () => import("@/views/lead-distribution/spam-settings/SpamSettings.vue"),
        meta: {
          pageTitle: "Spam Settings",
          breadcrumbs: ["Settings", "Spam Settings"],
        },
      },
      {
        path: "test-leads",
        name: "testLeads",
        component: () => import("@/views/settings/test-leads/TestLeads.vue"),
        meta: {
          pageTitle: "Test Leads",
          breadcrumbs: ["Settings", "Test Leads"],
        },
      },
      {
        path: "retainer-dupe-settings",
        name: "retainer-dupe-settings",
        component: () => import("@/views/lead-distribution/retainer-dupe-settings/RetainerDupeSettings.vue"),
        meta: {
          pageTitle: "Retainer Duplicate Settings",
          breadcrumbs: ["Settings", "Retainer Duplicate Settings"],
        },
      },
    ],
  },
  {
    path: "/analysis",
    name: "analysis",
    children: [
      {
        path: "transcript-analysis",
        name: "transcript-analysis",
        component: () => import("@/views/analytics/transcript-analysis/TranscriptAnalysisList.vue"),
        meta: {
          pageTitle: "Transcript Analysis",
          breadcrumbs: ["Analysis", "Transcript Analysis"],
        },
      },
      {
        path: "transcript-analysis/create",
        name: "transcript-analysis-create",
        component: () => import("@/views/analytics/transcript-analysis/CreateTranscriptAnalysis.vue"),
        meta: {
          pageTitle: "Create Analysis",
          breadcrumbs: ["Analysis", "Transcript Analysis", "Create"],
        },
      },
      {
        path: "transcript-analysis/:id",
        name: "transcript-analysis-detail",
        component: () => import("@/views/analytics/transcript-analysis/TranscriptAnalysisDetail.vue"),
        meta: {
          pageTitle: "Analysis Details",
          breadcrumbs: ["Analysis", "Transcript Analysis", "Details"],
        },
      },
      {
        path: "transcript-analysis/templates",
        name: "transcript-analysis-templates",
        component: () => import("@/views/analytics/transcript-analysis/PromptTemplates.vue"),
        meta: {
          pageTitle: "Prompt Templates",
          breadcrumbs: ["Analysis", "Transcript Analysis", "Templates"],
        },
      },
      {
        path: "personas",
        name: "persona-list",
        component: () => import("@/views/persona/PersonaList.vue"),
        meta: {
          pageTitle: "Personas",
          breadcrumbs: ["Analysis", "Personas"],
        },
      },
      {
        path: "personas/generate",
        name: "persona-generate",
        component: () => import("@/views/persona/PersonaGenerate.vue"),
        meta: {
          pageTitle: "Generate Persona",
          breadcrumbs: ["Analysis", "Personas", "Generate"],
        },
      },
      {
        path: "personas/generate/:historyId",
        name: "persona-candidates",
        component: () => import("@/views/persona/PersonaCandidates.vue"),
        meta: {
          pageTitle: "Persona Candidates",
          breadcrumbs: ["Analysis", "Personas", "Candidates"],
        },
      },
      {
        path: "personas/history",
        name: "persona-history",
        component: () => import("@/views/persona/PersonaHistory.vue"),
        meta: {
          pageTitle: "Generation History",
          breadcrumbs: ["Analysis", "Personas", "History"],
        },
      },
      {
        path: "personas/targeting",
        name: "persona-targeting-list",
        component: () => import("@/views/persona/PersonaTargetingList.vue"),
        meta: {
          pageTitle: "META Targeting Conditions",
          breadcrumbs: ["Analysis", "Personas", "META Targeting"],
        },
      },
      {
        path: "personas/targeting/generate",
        name: "persona-targeting-generate",
        component: () => import("@/views/persona/PersonaTargetingGenerate.vue"),
        meta: {
          pageTitle: "Generate META Targeting",
          breadcrumbs: ["Analysis", "Personas", "META Targeting", "Generate"],
        },
      },
      {
        path: "personas/:id",
        name: "persona-detail",
        component: () => import("@/views/persona/PersonaDetail.vue"),
        meta: {
          pageTitle: "Persona Details",
          breadcrumbs: ["Analysis", "Personas", "Details"],
        },
      },
      {
        path: "creatives",
        name: "creative-list",
        component: () => import("@/views/creative/AiCreativeList.vue"),
        meta: {
          pageTitle: "AI Creatives",
          breadcrumbs: ["Analysis", "AI Creatives"],
        },
      },
      {
        path: "creatives/generate",
        name: "creative-generate",
        component: () => import("@/views/creative/CreativeGenerate.vue"),
        meta: {
          pageTitle: "Generate AI Creative",
          breadcrumbs: ["Analysis", "AI Creatives", "Generate"],
        },
      },
      {
        path: "creatives/generate/:id/results",
        name: "creative-results",
        component: () => import("@/views/creative/CreativeResults.vue"),
        meta: {
          pageTitle: "Creative Results",
          breadcrumbs: ["Analysis", "AI Creatives", "Results"],
        },
      },
    ],
  },
]

export default leadDistributionRoutes
